<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { toPlainText } from "@/utils/markdown";
import { blanksAsLabels } from "@/utils/blanks";
import { mentionsDates } from "@/utils/calendar";
import { emailParts, looksLikeMessage, mailtoLink } from "@/utils/email";
import CalendarEvents from "./CalendarEvents.vue";
import DownloadButton from "./DownloadButton.vue";
import { focusForTyping } from "@/utils/keyboard";
import { listBlanks } from "@/utils/blanks";

defineProps({
  // The slim copy pinned to the top of the page while scrolling
  compact: { type: Boolean, default: false },
});

const store = useStore();

const prompt = computed(() => store.state.prompt);
const generatedText = computed(() => store.state.result.generatedText);
const editMode = computed(() => store.state.result.editMode);
const isMobile = computed(() => store.state.isMobile);
const finishedText = computed(() => store.state.result.finishedText);

const hideResult = () => store.dispatch("result/hideResult");
const toggleEditMode = () => store.dispatch("result/toggleEditMode");
const openNewPrompt = () => store.dispatch("modals/openNewPrompt");

const output = computed(() => store.state.result.output);
// Documents are written in Markdown; copy and share them as clean text
const plainText = computed(() =>
  output.value === "text" || output.value === "image"
    ? blanksAsLabels(generatedText.value)
    : toPlainText(generatedText.value)
);

const copyToClipboard = () => {
  navigator.clipboard.writeText(plainText.value);
};

const loading = computed(() => store.state.result.loading);
// Edit, Share and the rest wait until the make has finished being written
const finished = computed(
  () => !loading.value && finishedText.value?.length > 0
);
const error = computed(() => store.state.result.error);
const image = computed(() => store.state.result.image);

// On computers the make widens over the tabs to read, and shrinks back to change answers
const expanded = computed(() => store.state.result.expanded);
const toggleExpanded = () => store.dispatch("result/toggleExpanded");

// Download and the blanks to fill in sit in the header, not above the make
const fileReady = computed(
  () =>
    !loading.value &&
    !error.value &&
    !editMode.value &&
    Boolean(generatedText.value) &&
    (output.value !== "image" || Boolean(image.value))
);
const blankCount = computed(() =>
  loading.value || editMode.value || output.value === "image"
    ? 0
    : listBlanks(generatedText.value).length
);
const fillBlanks = () => {
  // Focused during the tap, so a phone opens its keyboard straight away
  focusForTyping("blank-filler-input");
  store.dispatch("result/startFillingBlanks");
};

// Messages can go straight to the user's email app
const emailable = computed(
  () =>
    !loading.value &&
    looksLikeMessage(prompt.value, generatedText.value, output.value)
);
const emailNote = ref("");
const sendEmail = async () => {
  const parts = emailParts(plainText.value, prompt.value);
  const { url, complete } = mailtoLink(parts);
  emailNote.value = "";
  if (!complete) {
    // Too long for a link: copy it to paste into the email instead
    try {
      await navigator.clipboard.writeText(parts.body);
      emailNote.value = "Your make is copied, paste it into the email";
    } catch {
      emailNote.value = "Your make is too long to send this way, copy it";
    }
  }
  window.location.href = url;
};

// Makes with dates (itineraries, invitations, plans) can go in a calendar
const showCalendar = ref(false);
const hasDates = computed(
  () =>
    !loading.value &&
    output.value !== "image" &&
    mentionsDates(generatedText.value)
);

const loggedIn = computed(() => store.state.auth.loggedIn);
const shareLabel = ref("Share");
// Sharing also lets people remix the make, so say what they'll see
const shareNote = ref(false);

// Shares a link to the make: the device's share sheet where there is one,
// otherwise the link is copied
const startShare = async () => {
  shareLabel.value = "Sharing...";
  const url = await store.dispatch("result/shareLink");
  if (!url) {
    shareLabel.value = "Try again";
    return;
  }
  shareNote.value = true;
  if (navigator.share) {
    try {
      await navigator.share({ title: prompt.value, url });
      shareLabel.value = "Share";
      return;
    } catch {
      // Cancelled or unavailable, so fall back to copying the link
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    shareLabel.value = "Link copied";
  } catch {
    window.prompt("Copy this link to share your make", url);
    shareLabel.value = "Share";
  }
  setTimeout(() => (shareLabel.value = "Share"), 3000);
};
</script>

<template>
  <section>
    <button
      class="-ml-1 mt-3 flex items-center rounded-full px-1 py-1 text-sm font-bold text-accentPrimary lg:hidden"
      @click="hideResult"
    >
      <font-awesome-icon :icon="['fas', 'chevron-left']" class="mr-1.5" />
      Questions & style
    </button>
    <div
      class="w-full mt-3 mb-1 lg:mt-0 text-primary"
      :class="{ 'flex items-center justify-between gap-6': compact }"
    >
      <div
        class="flex items-start gap-3"
        :class="{ 'min-w-0 flex-1': compact }"
      >
        <!-- Long titles wrap onto a second line; the pinned header keeps one -->
        <h3
          class="min-w-0 flex-1 text-xl lg:text-2xl"
          :class="
            compact ? 'truncate' : 'line-clamp-2 [overflow-wrap:anywhere]'
          "
          :title="prompt"
        >
          {{ prompt }}
        </h3>
        <button
          v-if="!isMobile && !compact"
          id="shepherd-target-expand"
          type="button"
          class="mt-1 flex shrink-0 items-center rounded-full border border-gray-200 px-3 py-1.5 text-xs font-bold text-secondary transition-colors hover:border-primary hover:text-primary"
          :title="
            expanded
              ? 'Give the questions and styles more room again'
              : 'Give your make more room to read'
          "
          :aria-pressed="expanded"
          @click="toggleExpanded"
        >
          <font-awesome-icon
            :icon="[
              'fas',
              expanded
                ? 'down-left-and-up-right-to-center'
                : 'up-right-and-down-left-from-center',
            ]"
            class="mr-1.5"
          />
          {{ expanded ? "Shrink" : "Expand" }}
        </button>
      </div>
      <!-- One panel holds every action, so the blanks and Download sit with the rest -->
      <div
        v-if="finished"
        class="flex flex-wrap items-center gap-x-4 sm:gap-x-5"
        :class="
          compact
            ? 'shrink-0 gap-y-3'
            : 'mt-3 gap-y-2 rounded-2xl bg-gray-10 px-4 py-2.5'
        "
      >
        <button
          id="shepherd-target-make-edit"
          type="button"
          class="grid grid-cols-1 cursor-pointer"
          :class="editMode ? 'text-accentPrimary' : 'text-secondary'"
          :title="editMode ? 'Stop editing' : 'Edit your make'"
          :aria-pressed="editMode"
          @click="toggleEditMode"
        >
          <font-awesome-icon
            class="mx-auto"
            :icon="['fas', editMode ? 'check' : 'pencil']"
          />
          <span class="text-xs text-center font-bold mt-0.5">
            {{ editMode ? "Done" : "Edit" }}
          </span>
        </button>
        <button
          id="shepherd-target-make-reuse"
          type="button"
          class="grid grid-cols-1 text-secondary cursor-pointer"
          title="Start a new Make with this as the context"
          @click="openNewPrompt"
        >
          <font-awesome-icon
            class="mx-auto"
            :icon="['fas', 'arrows-split-up-and-left']"
          />
          <span class="text-xs text-center font-bold mt-0.5 whitespace-nowrap">
            Re-use
          </span>
        </button>
        <button
          v-if="loggedIn"
          type="button"
          class="grid grid-cols-1 text-secondary cursor-pointer"
          title="Share a link to your Make"
          @click="startShare"
        >
          <font-awesome-icon class="mx-auto" :icon="['fas', 'share-nodes']" />
          <span
            class="text-xs text-center font-bold mt-0.5 whitespace-nowrap"
            aria-live="polite"
            >{{ shareLabel }}</span
          >
        </button>
        <button
          type="button"
          class="grid grid-cols-1 text-secondary cursor-pointer"
          title="Copy your Make to the clipboard"
          @click="copyToClipboard"
        >
          <font-awesome-icon class="mx-auto" :icon="['fas', 'copy']" />
          <span class="text-xs text-center font-bold mt-0.5">Copy</span>
        </button>
        <button
          v-if="emailable && !compact"
          type="button"
          class="grid grid-cols-1 text-secondary cursor-pointer"
          title="Open this in your email app"
          @click="sendEmail"
        >
          <font-awesome-icon class="mx-auto" :icon="['far', 'envelope']" />
          <span class="text-xs text-center font-bold mt-0.5">Email</span>
        </button>
        <button
          v-if="hasDates && loggedIn && !compact"
          type="button"
          class="grid grid-cols-1 cursor-pointer"
          :class="showCalendar ? 'text-accentPrimary' : 'text-secondary'"
          title="Add the dates to your calendar"
          :aria-expanded="showCalendar"
          @click="showCalendar = !showCalendar"
        >
          <font-awesome-icon class="mx-auto" :icon="['fas', 'calendar-plus']" />
          <span class="text-xs text-center font-bold mt-0.5">Calendar</span>
        </button>
        <div
          v-if="fileReady"
          class="flex items-center gap-2"
          :class="
            compact
              ? 'ml-auto'
              : [
                  'w-full border-t border-gray-200 pt-2 sm:ml-auto sm:w-auto sm:border-0 sm:pt-0',
                  blankCount ? 'justify-between' : 'justify-end',
                ]
          "
        >
          <button
            v-if="blankCount && !compact"
            type="button"
            class="flex items-center rounded-full bg-yellow-300 px-3 py-2 text-sm font-bold text-primary transition-colors hover:bg-yellow-200"
            :aria-label="`${blankCount} ${
              blankCount === 1 ? 'blank' : 'blanks'
            } to fill in`"
            :title="`Fill in the ${blankCount} details the genie didn't know`"
            @click="fillBlanks"
          >
            <font-awesome-icon
              :icon="['fas', 'pencil']"
              class="mr-1.5 text-xs"
            />
            {{ blankCount
            }}<span :class="compact ? 'hidden xs:inline' : ''"
              >&nbsp;{{ blankCount === 1 ? "blank" : "blanks" }}</span
            >
          </button>
          <DownloadButton
            :compact="compact"
            :output="output || 'text'"
            :text="generatedText"
            :title="prompt"
            :image="image"
          />
        </div>
      </div>
      <p
        v-if="shareNote && !compact"
        class="mt-2 text-sm text-secondary"
        role="status"
      >
        Anyone with the link can see this make and remix it with the answers,
        context and documents you gave it, but never your profile details or
        photos. You can stop sharing from My makes.
      </p>
      <p
        v-if="emailNote && !compact"
        class="mt-2 text-sm text-secondary"
        role="status"
      >
        {{ emailNote }}
      </p>
      <CalendarEvents
        v-if="showCalendar && hasDates && !compact"
        :key="generatedText"
        :text="plainText"
        :title="prompt"
        @close="showCalendar = false"
      />
    </div>
  </section>
</template>
